<script lang="ts">
	import photo3 from "@assets/final_photos/DSC_0103.jpg?webp&w=620&quality=65&imagetools";
	import photo4 from "@assets/final_photos/DSC_0304.jpg?webp&w=620&quality=65&imagetools";
	import photo2 from "@assets/final_photos/DSC_0311.jpg?webp&w=620&quality=65&imagetools";
	import photo1 from "@assets/final_photos/DSC_9989.jpg?webp&w=620&quality=65&imagetools";
	import { onDestroy, onMount } from "svelte";
	import { getTranslate } from "../utils/getTranslate.js";
	import translation from "./WhyCooperateCarousel.translations.json" with { type: "json" };
	import Formatted from "./common/Formatted.svelte";
	import Media from "./common/Media.svelte";

	const translate = getTranslate(translation);

	interface Slide {
		image: string;
		title: string;
		description: string;
	}

	const slides: Slide[] = [
		{
			image: photo1,
			title: translate("slide1Title"),
			description: translate("slide1Description"),
		},
		{
			image: photo2,
			title: translate("slide2Title"),
			description: translate("slide2Description"),
		},
		{
			image: photo3,
			title: translate("slide3Title"),
			description: translate("slide3Description"),
		},
		{
			image: photo4,
			title: translate("slide4Title"),
			description: translate("slide4Description"),
		},
	];

	const playIntervalDuration = 8000;

	let selectedIndex = 0;
	let isTransitioning = false;
	let containerRef: HTMLDivElement;
	let isMobile = false;

	const activeSlideWidth = 720;
	const inactiveSlideWidth = 340;
	const slideGap = 16;

	let progress = 0;
	let autoplayInterval: ReturnType<typeof setInterval> | null = null;
	let progressInterval: ReturnType<typeof setInterval> | null = null;

	function startProgressAnimation(): void {
		const startTime = Date.now();
		progressInterval = setInterval(() => {
			const elapsed = Date.now() - startTime;
			progress = Math.min((elapsed / playIntervalDuration) * 100, 100);

			if (progress >= 100) {
				if (progressInterval) {
					clearInterval(progressInterval);
				}
			}
		}, 16);
	}

	function resetProgress(): void {
		progress = 0;
		if (progressInterval) {
			clearInterval(progressInterval);
		}
		startProgressAnimation();
	}

	function goToNext(): void {
		if (isTransitioning) {
			return;
		}
		isTransitioning = true;
		selectedIndex = selectedIndex === slides.length - 1 ? 0 : selectedIndex + 1;
		resetProgress();
		setTimeout(() => {
			isTransitioning = false;
		}, 500);
	}

	function getSlidePosition(targetIndex: number): number {
		const effectiveIndex = targetIndex === slides.length - 1 ? slides.length - 2 : targetIndex;

		let position = 0;
		for (let i = 0; i < effectiveIndex; i++) {
			const isActive = i === selectedIndex;
			position += (isActive ? activeSlideWidth : inactiveSlideWidth) + slideGap;
		}
		return position;
	}

	$: if (typeof window !== "undefined") {
		isMobile = window.innerWidth < 768;
	}

	function getMobileTransform(): string {
		if (typeof window === "undefined") {
			return "translateX(0)";
		}
		const slideWidth = window.innerWidth - 128;
		const gapSize = 16;

		// Pro poslední slide zarovnat doprava tak, aby byla vidět půlka předposledního
		if (selectedIndex === slides.length - 1) {
			const containerWidth = window.innerWidth;
			const totalWidth = slides.length * (slideWidth + gapSize) - gapSize;
			const offset = totalWidth - containerWidth + 64; // 64 = padding (32px z každé strany)
			return `translateX(-${offset}px)`;
		}

		const offset = selectedIndex * (slideWidth + gapSize);
		return `translateX(-${offset}px)`;
	}

	$: transformValue = isMobile ? getMobileTransform() : `translateX(-${getSlidePosition(selectedIndex)}px)`;

	function goToSlide(index: number): void {
		if (isTransitioning) {
			return;
		}
		isTransitioning = true;
		selectedIndex = index;
		resetProgress();

		if (autoplayInterval) {
			clearInterval(autoplayInterval);
		}
		if (!isMobile) {
			autoplayInterval = setInterval(goToNext, playIntervalDuration);
		}

		setTimeout(() => {
			isTransitioning = false;
		}, 500);
	}

	function setupAutoplay(): void {
		if (typeof window !== "undefined" && window.innerWidth >= 768) {
			autoplayInterval = setInterval(goToNext, playIntervalDuration);
			startProgressAnimation();
		}
	}

	function handleResize(): void {
		if (typeof window !== "undefined") {
			const wasMobile = isMobile;
			isMobile = window.innerWidth < 768;

			if (wasMobile !== isMobile) {
				if (autoplayInterval) {
					clearInterval(autoplayInterval);
					autoplayInterval = null;
				}
				if (progressInterval) {
					clearInterval(progressInterval);
					progressInterval = null;
				}
				if (!isMobile) {
					setupAutoplay();
				}
			}
		}
	}

	let lastScrollTime = 0;
	const scrollThrottle = 500; // ms mezi přepnutími

	let touchStartX = 0;
	let touchEndX = 0;
	const minSwipeDistance = 50;

	function handleTouchStart(event: TouchEvent): void {
		if (!isMobile || !event.touches[0]) {
			return;
		}
		touchStartX = event.touches[0].clientX;
	}

	function handleTouchMove(event: TouchEvent): void {
		if (!isMobile || !event.touches[0]) {
			return;
		}
		touchEndX = event.touches[0].clientX;
	}

	function handleTouchEnd(): void {
		if (!isMobile) {
			return;
		}

		const now = Date.now();
		if (now - lastScrollTime < scrollThrottle) {
			return;
		}

		const swipeDistance = touchStartX - touchEndX;

		if (Math.abs(swipeDistance) < minSwipeDistance) {
			return;
		}

		lastScrollTime = now;

		if (swipeDistance > 0) {
			goToNext();
		} else {
			if (isTransitioning) {
				return;
			}
			isTransitioning = true;
			selectedIndex = selectedIndex === 0 ? slides.length - 1 : selectedIndex - 1;
			resetProgress();

			setTimeout(() => {
				isTransitioning = false;
			}, 500);
		}
	}

	function handleWheel(event: WheelEvent): void {
		if (!isMobile) {
			return;
		}

		const now = Date.now();
		if (now - lastScrollTime < scrollThrottle) {
			return;
		}

		const isHorizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY);
		const delta = isHorizontal ? event.deltaX : event.deltaY;

		if (Math.abs(delta) < 10) {
			return;
		}

		lastScrollTime = now;

		if (delta > 0) {
			goToNext();
		} else {
			if (isTransitioning) {
				return;
			}
			isTransitioning = true;
			selectedIndex = selectedIndex === 0 ? slides.length - 1 : selectedIndex - 1;
			resetProgress();

			// Restart autoplay (už víme, že jsme na desktopu díky kontrole na začátku handleWheel)
			if (autoplayInterval) {
				clearInterval(autoplayInterval);
			}
			autoplayInterval = setInterval(goToNext, playIntervalDuration);

			setTimeout(() => {
				isTransitioning = false;
			}, 500);
		}
	}

	onMount(() => {
		setupAutoplay();
		handleResize();
		if (typeof window !== "undefined") {
			window.addEventListener("resize", handleResize);
		}
	});

	onDestroy(() => {
		if (autoplayInterval) {
			clearInterval(autoplayInterval);
		}
		if (progressInterval) {
			clearInterval(progressInterval);
		}
		if (typeof window !== "undefined") {
			window.removeEventListener("resize", handleResize);
		}
	});
</script>

<section class="full-width grid-container bg-zms-grey">
	<div
		class="flex flex-col gap-6 sm:flex-row justify-between items-start sm:items-end mb-8 sm:mb-12 md:mb-16 lg:mb-20 px-2 sm:px-0"
	>
		<h2 class="w-full xl:w-1/2 text-left">
			<Formatted text={translate("title")} />
		</h2>

		<div
			class="hidden md:flex items-center gap-1 sm:gap-1.5 border h-min border-zms-blue rounded-full p-0.5 sm:p-1 mx-auto sm:mx-0"
		>
			{#each slides as _, index (index)}
				<button
					class="relative h-7 sm:h-8 md:h-10 {index === selectedIndex
						? 'w-12 sm:w-14 md:w-16 lg:w-20'
						: 'w-7 sm:w-8 md:w-10 border-zms-blue border'} rounded-full bg-white flex items-center justify-center text-xs sm:text-sm md:text-base/[1.2] overflow-hidden cursor-pointer"
					onclick={(): void => {
						goToSlide(index);
					}}
				>
					<span class="z-10 text-zms-blue font-medium">{index + 1}</span>
					{#if index === selectedIndex}
						<div
							class="absolute left-0 top-0 h-full bg-[#CCCCFF] z-0 transition-all duration-100"
							style="width: {progress}%"
						></div>
					{/if}
				</button>
			{/each}
		</div>
	</div>
	<div class={isMobile ? "" : "container-right md:container-right"}>
		<div class="col-span-full overflow-hidden">
			<div
				class="relative"
				bind:this={containerRef}
				onwheel={handleWheel}
				ontouchstart={handleTouchStart}
				ontouchmove={handleTouchMove}
				ontouchend={handleTouchEnd}
			>
				<div
					class="flex transition-transform duration-500 ease-in-out {isMobile ? 'gap-4' : 'gap-2 md:gap-4 lg:gap-6'}"
					style="transform: {transformValue}"
				>
					{#each slides as slideData, index (index)}
						{@const isActiveSlide = index === selectedIndex}
						<div class={isMobile ? "w-[calc(100vw-8rem)] flex-shrink-0" : ""}>
							<button
								class="flex-none {isMobile
									? 'h-[18rem] w-full'
									: 'h-[20rem] sm:h-[24rem] md:h-[28rem] lg:h-[32rem] w-full'} relative rounded-xl sm:rounded-2xl overflow-hidden group transition-all duration-500 ease-in-out cursor-pointer outline-none"
								class:md:w-[720px]={isActiveSlide && !isMobile}
								class:md:w-[340px]={!isActiveSlide && !isMobile}
								onclick={(): void => {
									goToSlide(index);
								}}
								tabindex="0"
								onkeydown={(event: KeyboardEvent): void => {
									if (event.key === "Enter" || event.key === " ") {
										goToSlide(index);
									}
								}}
							>
								<div class="absolute top-3 sm:top-4 md:top-6 left-3 sm:left-4 md:left-6 z-30">
									<span class="text-white text-2xl sm:text-3xl md:text-4xl lg:text-7xl font-light leading-none">
										{index + 1}
									</span>
								</div>

								<div class="relative w-full h-full">
									<Media
										class="absolute inset-0 w-full h-full object-cover"
										source={{ src: slideData.image, type: "image/webp" }}
										width={0}
										height={620}
										alt={slideData.title}
									/>
									<div class="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent"></div>
								</div>

								<div class="absolute text-start bottom-0 left-0 right-0 p-3 sm:p-4 md:p-6 z-20">
									<h3 class="text-white text-sm sm:text-base md:text-lg lg:text-[2rem]/[1.2] font-medium">
										<Formatted text={slideData.title} />
									</h3>
								</div>
								<div class="absolute inset-0 bg-gradient-to-t from-white/10 via-transparent to-transparent"></div>
								<div
									class="absolute inset-0 backdrop-blur-lg [mask:linear-gradient(transparent,transparent,white,white)]"
								></div>
							</button>
							<p class="text-zms-optical-black text-xs/[1.1] sm:text-sm lg:text-sm p-3 sm:p-4 md:p-6">
								<Formatted text={slideData.description} />
							</p>
						</div>
					{/each}
				</div>
			</div>
		</div>
	</div>
</section>
